import {
  booleanAttribute,
  Component,
  ContentChild,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { CdkConnectedOverlay, CdkOverlayOrigin, ConnectedPosition } from '@angular/cdk/overlay';
import { RegexpSearch } from '../../../shared/helpers/regex';
import { mod } from '../../../shared/helpers/math';
import { NgClass, NgForOf, NgIf, NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ClickOutsideDirective } from '../../directives/click-outside.directive';
import { DisableScrollContainerDirective } from '../../../tenders/directives/disabled-scroll-container.directive';

export interface DropdownItem<T = any> extends DisplayItem<T> {
  description?: string;
  icon?: string;
  disabled?: boolean;
  meta?: object;
}

export interface DisplayItem<T> {
  label: string;
  value: T;
}

@Directive({
  selector: '[exDropdownFooter]',
  standalone: true,
})
export class ExDropdownFooterDirective {
  constructor(public template: TemplateRef<any>) {}
}

@Directive({
  selector: '[exDropdownContent]',
  standalone: true,
})
export class ExDropdownContentDirective {
  constructor(public template: TemplateRef<any>) {}
}

@Directive({
  selector: '[exDropdownItem]',
  standalone: true,
})
export class ExDropdownItemDirective {
  constructor(public template: TemplateRef<{ $implicit: DropdownItem }>) {}
}

@Directive({
  selector: '[exDropdownDisplay]',
  standalone: true,
})
export class ExDropdownDisplayDirective {
  constructor(public template: TemplateRef<{ $implicit: string }>) {}
}
@Directive({
  selector: '[exDropdownDisplayLeftPart]',
  standalone: true,
})
export class ExDropdownDisplayLeftPart {
  constructor(public template: TemplateRef<any>) {}
}
@Directive({
  selector: '[exDropdownDisplayClearIcon]',
  standalone: true,
})
export class ExDropdownDisplayClearIcon {
  constructor(public template: TemplateRef<any>) {}
}

@Component({
  selector: 'ex-dropdown',
  standalone: true,
  templateUrl: './ex-dropdown.component.html',
  imports: [
    NgClass,
    CdkOverlayOrigin,
    NgTemplateOutlet,
    FormsModule,
    CdkConnectedOverlay,
    ClickOutsideDirective,
    NgIf,
    NgForOf,
    DisableScrollContainerDirective,
  ],
  styleUrls: ['./ex-dropdown.component.scss'],
})
export class ExDropdownComponent implements OnChanges, OnInit {
  @ContentChild(ExDropdownContentDirective) contentTemplate?: ExDropdownContentDirective;
  @ContentChild(ExDropdownItemDirective) itemTemplate?: ExDropdownItemDirective;
  @ContentChild(ExDropdownFooterDirective) footerTemplate?: ExDropdownFooterDirective;
  @ContentChild(ExDropdownDisplayDirective) displayTemplate?: ExDropdownDisplayDirective;
  @ContentChild(ExDropdownDisplayLeftPart) iconLeftTemplate?: ExDropdownDisplayLeftPart;
  @ContentChild(ExDropdownDisplayClearIcon) clearIcon?: ExDropdownDisplayClearIcon;

  @Input() icon?: string;
  @Input() label?: string;
  @Input() placeholder?: string;
  @Input({ transform: booleanAttribute }) mustSelect?: boolean;
  @Input({ transform: booleanAttribute }) multiple?: boolean;
  @Input({ transform: booleanAttribute }) noInput?: boolean;
  @Input({ transform: booleanAttribute }) noInputMargin?: boolean;
  @Input({ transform: booleanAttribute }) strictWidth?: boolean;
  @Input({ transform: booleanAttribute }) largerInside?: boolean;
  @Input({ transform: booleanAttribute }) largeBorder?: boolean;
  @Input({ transform: booleanAttribute }) disabled?: boolean;
  @Input({ transform: booleanAttribute }) replaceChevron?: boolean;
  @Input({ transform: booleanAttribute }) noChevron?: boolean;
  @Input({ transform: booleanAttribute }) blackStyle?: boolean;
  @Input({ transform: booleanAttribute }) transparentStyle?: boolean;
  @Input({ transform: booleanAttribute }) boldInputText?: boolean;
  @Input({ transform: booleanAttribute }) noBorder?: boolean;
  @Input({ transform: booleanAttribute }) shadowBorder?: boolean;
  @Input({ transform: booleanAttribute }) softBorder?: boolean;
  @Input({ transform: booleanAttribute }) ignoreEnterKey?: boolean;
  @Input({ transform: booleanAttribute }) paddedItemsContainer?: boolean;
  @Input({ transform: booleanAttribute }) normalPlaceholderFontWeight?: boolean;
  @Input({ transform: booleanAttribute }) smallLeftMarginInput?: boolean;
  @Input({ transform: booleanAttribute }) setCursorToEnd?: boolean;
  @Input({ transform: booleanAttribute }) disableAutoClose = false;
  @Input({ transform: booleanAttribute }) contentSized = false;
  @Input() inputText?: string;
  @Input() defaultValues?: string[];
  @Input() flatSide?: 'left' | 'right';
  @Input() dropdownPlacement: 'left' | 'right' = 'right';
  @Input() size: 'x-small' | 'small' | 'medium' = 'medium';
  @Input() dropdownHeaderSize: 'x-small' | 'small' | 'medium' = 'medium';
  @Input() container: 'container' | 'body' = 'container';
  @Input() headerColor?: string = '';
  @Input() headerBackgroundColor?: string = '';
  @Input() chevronVisibility: 'never' | 'always' | 'hover' = 'always';
  @Input() inputWidth?: number;
  @Input() items?: DropdownItem[];
  @Input() forceItem$?: EventEmitter<DropdownItem | null | undefined>;
  @Input() overlayPositions: ConnectedPosition[] = [];
  @Input() dropdownMenuClass: string = '';
  @Input() dropdownDisplayTemplate?: TemplateRef<any>;
  @Input() inputCursor: 'pointer' | 'text' = 'pointer';

  @Input() values?: string[];
  @Input() forceToggle$?: EventEmitter<boolean>;
  @Output() valuesChange = new EventEmitter<string[] | undefined>();

  @Output() valueChange = new EventEmitter<DropdownItem | null>();
  @Output() textChange = new EventEmitter<string>();
  @Output() dropdownToggle = new EventEmitter<boolean>();
  @Output() outsideClicked = new EventEmitter<boolean>();
  @Output() clearChange = new EventEmitter<boolean>();

  text: string = '';
  selection?: DropdownItem | DropdownItem[];
  show = false;
  filteredItems?: DropdownItem[];

  @ViewChild('input') input?: ElementRef;
  @ViewChild('itemsContainer') itemsContainer?: ElementRef;
  focusIndex?: number;

  @HostBinding('class.ex-dropdown--content-sized')
  get contentSizedHostClass(): boolean {
    return this.contentSized;
  }

  constructor(private elementRef: ElementRef) {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['inputText'] && this.inputText) {
      this.text = this.inputText;
    }
    if (changes['defaultValues']) {
      this.selection = this.items?.filter(item => this.defaultValues?.includes(item.value.toString()));
      if (!this.inputText) {
        this.text = this.selection?.map(item => item.label).join(', ') ?? '';
        this.update();
      }
    }
    if (changes['values']) {
      this.selection = this.items?.filter(item => this.values?.includes(item.value.toString()));
      if (!this.inputText) {
        this.text = this.selection?.map(item => item.label).join(', ') ?? '';
        this.update();
      }
    }
    if (changes['forceToggle$'] && this.forceToggle$) {
      this.forceToggle$.subscribe(show => {
        this.toggle(show);
        this.input?.nativeElement?.blur();
      });
    }
  }

  ngOnInit() {
    // ce forceItem permet de gérer des cas où la valeur de item change depuis le composant parent
    // mais sans notifier le composant ex-dropdown.
    this.forceItem$?.subscribe(item => {
      this.select(item ?? null, !item);
    });
  }

  toggle(show?: boolean) {
    const newState = show ?? (!this.show || this.input?.nativeElement === document.activeElement);
    if (this.show !== newState) this.dropdownToggle.emit(newState);
    this.show = newState;
    if (this.show) {
      // focus text input field when opening dropdown with input.
      if (this.elementRef.nativeElement.querySelector('input[type="text"]')) {
        setTimeout(() => this.elementRef.nativeElement.querySelector('input[type="text"]')?.focus(), 50);
      }
      this.update();
    }
  }

  update(value?: string) {
    const newValue = value?.trim() || this.text.trim();
    this.textChange.emit(newValue);
    if (newValue) {
      if (this.selection && !(this.selection instanceof Array) && newValue === this.selection.label.trim()) this.filteredItems = this.items;
      else this.filteredItems = this.items?.filter(item => RegexpSearch.normalizedSearch(item.label, newValue));
    } else {
      this.select(null);
      this.filteredItems = this.items;
    }
    this.focusIndex = 0;
  }

  select(item: DropdownItem | null, propagate = true) {
    if (this.selection == item) return;
    this.selection = item || undefined;
    this.values = this.selection?.value;
    this.valuesChange.emit(this.values);
    this.text = item?.label || '';
    this.focusIndex = undefined;
    if (propagate) this.toggle(false);
    if (propagate) this.update();
    if (propagate) this.valueChange.emit(item);
  }

  clear() {
    this.text = '';
    this.textChange.emit('');
    this.select(null);
    this.clearChange.emit(true);
  }

  isDisabled(item: DropdownItem) {
    return (
      item.disabled ||
      !!(
        this.selection &&
        ((this.selection instanceof Array && this.selection.includes(item)) ||
          (!(this.selection instanceof Array) && this.selection.value === item.value))
      )
    );
  }

  @HostListener('document:keydown.ArrowUp', ['$event'])
  @HostListener('document:keydown.ArrowDown', ['$event'])
  onArrow(event: KeyboardEvent) {
    if (!this.filteredItems) {
      return;
    }
    event.preventDefault();
    if (!this.show && this.input?.nativeElement === document.activeElement) return this.toggle(true);
    if (this.focusIndex !== undefined) {
      this.focusIndex = mod(this.focusIndex + (event.key === 'ArrowUp' ? -1 : 1), this.filteredItems.length);
      this.itemsContainer?.nativeElement.children[this.focusIndex]?.scrollIntoView({ block: 'nearest' });
    } else {
      this.focusIndex = event.key === 'ArrowUp' ? this.filteredItems.length - 1 : 0;
    }
  }

  clickOutside() {
    this.outsideClicked.emit(true);
    if (!this.disableAutoClose) this.toggle(false);
  }

  @HostListener('document:keydown.Enter', ['$event'])
  @HostListener('document:keydown.Tab', ['$event'])
  onEnter(event: KeyboardEvent) {
    if (this.show && !this.ignoreEnterKey) {
      event.preventDefault();
      if (this.focusIndex !== undefined) {
        this.select(this.filteredItems?.[this.focusIndex] || null);
      }
    }
  }

  @HostListener('document:keydown.Escape', ['$event'])
  onEscape(event: KeyboardEvent) {
    if (event.target instanceof Element && event.target.tagName === 'INPUT') {
      event.preventDefault();
      this.toggle(false);
    }
  }

  @HostListener('mousemove')
  onMouseMove() {
    this.focusIndex = undefined;
  }
}
